/**
 * Everything the pipeline produced, as files.
 *
 * Three groups, because they are three different things and mixing them is how
 * a reader ends up downloading a diagnostic manifest thinking it is the
 * transcript:
 *
 *   audio        the mixed playback track and the per-speaker stems
 *   documents    transcript, activity, metrics — the JSON this screen renders
 *   diagnostics  per-source analysis, the manifest, and any kind this build
 *                does not recognise, folded away
 *
 * ===========================================================================
 * WHICH CLOCK EACH FILE IS ON — the one thing here that can be silently WRONG
 *
 * `AudioArtifactStatus.timebase` is now a published enum, and this panel reads
 * it per file instead of carrying a paragraph of prose about stems:
 *
 *   session       shares the mixed playback timeline. The transcript, the
 *                 activity ribbon and the player's clock all agree.
 *   speech_gated  that speaker's speech with the silence CUT OUT. Second 30 of
 *                 the file is not second 30 of the call, and its `duration_ms`
 *                 is their speaking time rather than the call's length.
 *   source        relative to one decoded recording, which begins when that
 *                 participant's track did — not when the call did.
 *   none          no timeline at all (the manifest).
 *
 * A stem carries `mapping` (`StemTimelineMapping[]`) and it is the ONLY record
 * of where the silence was removed, so it is the only way to put a stem back
 * where it came from. Its spans are on the SOURCE timeline, not the session's,
 * which is why the line below says "of that speaker's own recording" and not
 * "of the call" — the extra offset between a source and the session is a
 * participant's join time, and no artifact field states it.
 *
 * Nothing here plays a stem, so nothing here can mis-align one. What it must
 * not do is present the numbers as if they were call time, and that is what
 * the per-row label and the reworded duration are for. Anything that ever DOES
 * offer stem playback has to seek through `mapping`, not straight through.
 * ===========================================================================
 *
 * Every link goes through `/api/sessions/:id/artifacts/:artifactId`, which is
 * this app's proxy in front of the control plane's authenticated, audited one.
 * There is no B2 credential in this application, no presigned URL, and no
 * direct bucket access — the bytes are fetched by a server that holds an API
 * key the browser has never seen.
 */

import type * as React from 'react'
import {
  ChevronRight,
  Download,
  FileJson,
  Music,
  Scissors,
  Video,
  Waves,
} from 'lucide-react'
import { isSessionAligned, type ArtifactRef } from '@era/shared'
import { Badge } from '@/components/ui/badge'
import { artifactUrl } from '@/lib/api'
import { formatBytes, formatDuration } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { SpeakerRoster } from '../speakers'
import type { VideoState } from '../video'

const AUDIO_KINDS = new Set<string>(['playback', 'stem'])
const DOCUMENT_KINDS = new Set<string>(['transcript', 'activity', 'metrics'])

export function FilesPanel({
  sessionId,
  artifacts,
  video,
  roster,
}: {
  sessionId: string
  artifacts: ArtifactRef[]
  /**
   * Retained video, which is NOT in `artifacts[]` and never will be.
   *
   * It is an independent channel with its own pipeline and its own route, so it
   * does not appear in `audio_pipeline.artifacts[]`. This panel is where a
   * reader comes to find out what a call left behind, so leaving video out of
   * it would make "is there video?" unanswerable from the one screen that is
   * supposed to answer it.
   */
  video: VideoState
  roster: SpeakerRoster
}) {
  const audio = artifacts.filter((artifact) => AUDIO_KINDS.has(artifact.kind))
  const documents = artifacts.filter((artifact) => DOCUMENT_KINDS.has(artifact.kind))
  // Everything else, INCLUDING a kind this build has never heard of. An
  // unrecognised kind sorts here rather than throwing or being dropped: the
  // enum is closed today, and a client that hides a file it cannot classify is
  // a client that quietly loses the pipeline's next output.
  const diagnostics = artifacts.filter(
    (artifact) => !AUDIO_KINDS.has(artifact.kind) && !DOCUMENT_KINDS.has(artifact.kind),
  )

  // The video section is drawn even when there is no video, and that is
  // deliberate — see `VideoFile`. So an empty `artifacts[]` is no longer an
  // empty panel unless the video answer is empty too.
  if (artifacts.length === 0 && video.kind === 'absent') {
    return (
      <p className="rounded-xl border border-dashed border-line bg-surface-2/40 px-6 py-10 text-center text-sm text-ink-muted">
        No files yet. The pipeline writes them when it finishes, and no video was retained for this
        call.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      {/* First, because it is the largest thing a call produces and the one a
          reader is most likely to have come looking for. */}
      <Group
        title="Video"
        note="Retained video is the selected-video child of the unified recording policy. It is not one of the per-speaker audio artifacts: it is one composed MP4 whose clock starts at the first video-enabled boundary, and the player translates that published wall-clock origin when aligning transcript and activity."
      >
        <VideoFile sessionId={sessionId} video={video} />
      </Group>

      {audio.length > 0 ? (
        <Group
          title="Audio"
          note="Only the mixed track is on the call's timeline. Each stem is one speaker's speech with the silence removed, so it is shorter than the call and nothing in it lines up with the transcript — every row says which clock it is on."
        >
          {audio.map((artifact) => (
            <Row
              key={artifact.artifact_id}
              sessionId={sessionId}
              artifact={artifact}
              who={artifact.identity ? roster.get(artifact.identity).displayName : null}
              icon={artifact.kind === 'playback' ? <Waves /> : <Music />}
            />
          ))}
        </Group>
      ) : null}

      {documents.length > 0 ? (
        <Group
          title="Documents"
          note="What this screen is drawn from. The transcript carries word-level timings; the activity document carries the speech segments behind the ribbon. Both use the audio/session timeline; retained video publishes its own origin so this screen can translate them exactly."
        >
          {documents.map((artifact) => (
            <Row
              key={artifact.artifact_id}
              sessionId={sessionId}
              artifact={artifact}
              who={null}
              icon={<FileJson />}
            />
          ))}
        </Group>
      ) : null}

      {/* `list-none` removed the native marker and nothing replaced it, so this
          rendered as an inert grey heading with a gap under it — it read as a
          section that had failed to load rather than one that was folded. The
          chevron is the affordance. */}
      {diagnostics.length > 0 ? (
        <details className="group">
          <summary className="eyebrow flex cursor-pointer list-none items-center gap-1.5 py-2 hover:text-ink [&::-webkit-details-marker]:hidden">
            <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" />
            diagnostics · {diagnostics.length} files
          </summary>
          <div className="space-y-1.5 pt-2">
            {diagnostics.map((artifact) => (
              <Row
                key={artifact.artifact_id}
                sessionId={sessionId}
                artifact={artifact}
                who={artifact.identity ? roster.get(artifact.identity).displayName : null}
                icon={<FileJson />}
              />
            ))}
          </div>
          <p className="pt-3 text-[0.6875rem] leading-relaxed text-ink-dim">
            Per-source analysis and the run manifest. Pipeline internals, not product surface —
            here because an operator debugging a bad transcript needs them and should not have to
            reach for a bucket browser.
          </p>
        </details>
      ) : null}

      <p className="border-t border-line pt-4 text-[0.6875rem] leading-relaxed text-ink-dim">
        Every file streams through this app&rsquo;s server and then the control plane&rsquo;s
        authenticated proxy. No storage credential exists in this application, and none is ever
        sent to a browser.
      </p>
    </div>
  )
}

/**
 * The retained-video row — and it renders for ALL FIVE states, including "there
 * is none".
 *
 * Drawing nothing when there is no video was the first version and it was
 * wrong. "No video" and "this build does not know about video" look identical
 * from the outside, and the second is what a reader assumes when a feature they
 * have heard of leaves no trace on the screen that lists everything a call
 * produced. A stated absence is a working feature; a silent one is a bug
 * report.
 *
 * The tones are chosen so a reader can tell the three apart at a glance without
 * reading a word: neutral for a normal absence, accent for a wait that will
 * resolve itself, warn for a fault that will not.
 */
function VideoFile({ sessionId, video }: { sessionId: string; video: VideoState }) {
  if (video.kind === 'ready') {
    const { render } = video
    return (
      <div className="flex items-start gap-3 rounded-lg border border-line bg-surface px-4 py-2.5">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-2 text-ink-muted [&_svg]:size-4">
          <Video />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[0.8125rem] font-medium text-ink">selected video</span>
            {render.width && render.height ? (
              <Badge tone="neutral">
                {render.width}×{render.height}
              </Badge>
            ) : null}
            {/* The composite shares the session timeline, so unlike a stem it
                needs no alignment warning — it needs the opposite, stated once
                in the group note above. */}
            {video.caveat ? <Badge tone="warn">check the note</Badge> : null}
          </p>
          <p className="ident truncate text-[0.6875rem] text-ink-dim">{render.key}</p>
          {video.caveat ? (
            <p className="mt-1 text-[0.6875rem] leading-relaxed text-warn">{video.caveat}</p>
          ) : null}
        </div>
        <p className="mt-0.5 hidden shrink-0 text-right text-[0.6875rem] tabular text-ink-dim sm:block">
          {formatBytes(render.size_bytes ?? null)}
          {render.duration_ms ? ` · ${formatDuration(render.duration_ms)}` : ''}
        </p>
        <a
          // The same proxy route the player uses. `download` on a 720p MP4 is
          // the one place in this panel where the byte-range proxy is doing
          // real work — the browser will fetch it in chunks.
          href={`/api/sessions/${encodeURIComponent(sessionId)}/video`}
          download
          aria-label="Download the video"
          title="Download the video"
          className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md text-ink-dim transition-colors hover:bg-surface-2 hover:text-ink"
        >
          <Download className="size-4" />
        </a>
      </div>
    )
  }

  const tone =
    video.kind === 'failed' ? 'warn' : video.kind === 'pending' ? 'accent' : 'neutral'

  return (
    <div className="flex items-start gap-3 rounded-lg border border-line bg-surface px-4 py-2.5">
      <span
        className={cn(
          'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md [&_svg]:size-4',
          tone === 'warn' && 'bg-warn-soft text-warn',
          tone === 'accent' && 'bg-accent-soft text-accent',
          tone === 'neutral' && 'bg-surface-2 text-ink-muted',
        )}
      >
        <Video />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[0.8125rem] font-medium text-ink">{video.title}</p>
        <p className="mt-0.5 text-[0.6875rem] leading-relaxed text-ink-muted">{video.detail}</p>
      </div>
    </div>
  )
}

function Group({
  title,
  note,
  children,
}: {
  title: string
  note: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-2">
      <p className="eyebrow">{title}</p>
      <div className="space-y-1.5">{children}</div>
      <p className="text-[0.6875rem] leading-relaxed text-ink-dim">{note}</p>
    </section>
  )
}

function Row({
  sessionId,
  artifact,
  who,
  icon,
}: {
  sessionId: string
  artifact: ArtifactRef
  who: string | null
  icon: React.ReactNode
}) {
  const href = artifactUrl(sessionId, artifact.artifact_id)
  const isAudio = AUDIO_KINDS.has(artifact.kind)
  const gated = artifact.timebase === 'speech_gated'
  const span = sourceSpan(artifact)

  return (
    <div className="flex items-start gap-3 rounded-lg border border-line bg-surface px-4 py-2.5">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-2 text-ink-muted [&_svg]:size-4">
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[0.8125rem] font-medium text-ink">{artifact.kind}</span>
          {who ? <Badge tone="neutral">{who}</Badge> : null}
          {/* The alignment warning, on the row it applies to rather than in a
              paragraph under the group. `warn`, not `neutral`: a stem lined up
              against the transcript produces confident nonsense, and this badge
              is the only thing standing between a reader and that assumption. */}
          {isAudio && !isSessionAligned(artifact.timebase) ? (
            <Badge tone="warn" title={timebaseHint(artifact.timebase)}>
              <Scissors className="size-3" />
              {gated ? 'silence removed' : `${artifact.timebase ?? 'unknown'} timeline`}
            </Badge>
          ) : null}
          {artifact.state && artifact.state !== 'verified' ? (
            <Badge tone="warn">{artifact.state}</Badge>
          ) : null}
        </p>
        <p className="ident truncate text-[0.6875rem] text-ink-dim">{artifact.artifact_id}</p>
        {/* Where a speech-gated file came from, when the mapping says. Without
            it "0:38" is a length with no location and a reader will assume it
            starts where the call does. */}
        {gated && span ? (
          <p className="mt-1 text-[0.6875rem] leading-relaxed text-ink-dim">
            {formatDuration(artifact.duration_ms)} of speech, cut from{' '}
            {formatDuration(span.start)}–{formatDuration(span.end)} of that speaker&rsquo;s own
            recording across {span.count} {span.count === 1 ? 'span' : 'spans'}. Not the call
            timeline.
          </p>
        ) : gated ? (
          <p className="mt-1 text-[0.6875rem] leading-relaxed text-ink-dim">
            {formatDuration(artifact.duration_ms)} of speech with the gaps removed, and no mapping
            was stored — there is no way to say where in the call any of it happened.
          </p>
        ) : null}
      </div>

      {/* Duration only for audio. Every artifact carries `duration_ms` — the
          session's length — so a 3.9 kB transcript rendered as "3.9 kB · 0:32",
          which reads as a JSON file that is 32 seconds long. A document has a
          size and nothing else worth putting here.
          On a gated file it is already spelled out under the id, as speech
          rather than as call time, so the rail carries the size alone. */}
      <p className="mt-0.5 hidden shrink-0 text-right text-[0.6875rem] tabular text-ink-dim sm:block">
        {formatBytes(artifact.size_bytes)}
        {isAudio && !gated && artifact.duration_ms ? ` · ${formatDuration(artifact.duration_ms)}` : ''}
      </p>

      <a
        href={href}
        download
        aria-label={`Download ${artifact.kind}`}
        title={`Download ${artifact.kind}`}
        className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md text-ink-dim transition-colors hover:bg-surface-2 hover:text-ink"
      >
        <Download className="size-4" />
      </a>
    </div>
  )
}

/**
 * The stretch of the SOURCE recording a speech-gated stem was cut from.
 *
 * Min and max over the whole mapping rather than first and last: the array's
 * order is upstream's business, and a summary that assumes it is sorted is the
 * kind of quietly wrong alignment this whole file exists to avoid.
 */
function sourceSpan(artifact: ArtifactRef): { start: number; end: number; count: number } | null {
  const mapping = artifact.mapping
  if (!mapping || mapping.length === 0) return null
  let start = Number.POSITIVE_INFINITY
  let end = Number.NEGATIVE_INFINITY
  for (const entry of mapping) {
    if (entry.source_start_ms < start) start = entry.source_start_ms
    if (entry.source_end_ms > end) end = entry.source_end_ms
  }
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null
  return { start, end, count: mapping.length }
}

function timebaseHint(timebase: ArtifactRef['timebase']): string {
  switch (timebase) {
    case 'speech_gated':
      return 'This speaker’s speech with the silence cut out. Its timestamps are its own; nothing in it lines up with the transcript or the player.'
    case 'source':
      return 'Relative to one decoded recording, which starts when that participant’s track did — not when the call did.'
    case 'none':
      return 'No timeline at all.'
    default:
      return 'This file’s timebase was not recorded, so it must not be assumed to share the call’s timeline.'
  }
}
